"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LazyMotion, domAnimation, m, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { screenThumb } from "@/lib/firstparty/thumbs";
import { useSwipe } from "@/hooks/use-swipe";

export type LightboxItem = {
  src: string;
  alt?: string;
  caption?: string;
  /** Defaults to image. Videos use the same expanded viewer and controls. */
  type?: "image" | "video";
  poster?: string;
  /** External link (e.g. ad library source) shown as an action in the overlay. */
  href?: string;
  hrefLabel?: string;
  /** Optional second external action (e.g. landing page next to library link). */
  href2?: string;
  href2Label?: string;
};

// Full-screen, centered media overlay with left/right navigation across the
// whole set. It renders both screenshots and walkthrough video through a portal
// so the rails' overflow/mask never clips it.
export function ScreenLightbox({
  items,
  index,
  onClose,
  onIndex,
}: {
  items: LightboxItem[];
  index: number | null;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const open = index !== null && items.length > 0;
  const cur = index ?? 0;
  const many = items.length > 1;
  // Video autoplays muted and inline; with reduced motion it waits for the play control.
  const reduceMotion = useReducedMotion();
  // Index whose media can show. Until then a skeleton holds the stage; an error also counts, so the browser's own fallback shows.
  const [readyIndex, setReadyIndex] = useState<number | null>(null);
  const ready = readyIndex === cur;
  const markReady = () => setReadyIndex(cur);
  // The index whose stored copy failed to load and now shows the original.
  const [originalIndex, setOriginalIndex] = useState<number | null>(null);
  const original = originalIndex === cur;
  const setOriginal = (on: boolean) => setOriginalIndex(on ? cur : null);

  useEffect(() => {
    if (!open) return;
    const go = (delta: number) => onIndex((cur + delta + items.length) % items.length);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, cur, items.length, onIndex, onClose]);

  // Phones swipe between screens; the click that ends a swipe never closes the viewer.
  const swipe = useSwipe((delta) => many && onIndex((cur + delta + items.length) % items.length));
  if (typeof document === "undefined") return null;

  const item = open ? items[cur] : null;
  const step = (delta: number) => onIndex((cur + delta + items.length) % items.length);

  return createPortal(
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {item && (
          <m.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => !swipe.swiped() && onClose()}
            onTouchStart={swipe.onTouchStart}
            onTouchEnd={swipe.onTouchEnd}
            className="fixed inset-0 z-[100] flex items-center justify-center overscroll-x-none bg-black/80 backdrop-blur-sm p-4 sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-label={item.alt || "Screenshot"}
          >
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 grid place-items-center h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Prev / Next */}
            {many && (
              <>
                <ArrowBtn dir="left" onClick={(e) => { e.stopPropagation(); step(-1); }} />
                <ArrowBtn dir="right" onClick={(e) => { e.stopPropagation(); step(1); }} />
              </>
            )}

            <m.div
              initial={{ opacity: 0, scale: 0.94, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative flex flex-col items-center gap-3"
            >
              {/* Fixed stage: the skeleton and the media share it, so nothing moves when the media arrives. */}
              <div className="relative h-[80vh] w-[92vw]">
                {!ready && (
                  <Skeleton
                    role="status"
                    aria-label="Loading"
                    className="absolute inset-0 m-auto h-full max-w-[min(92vw,45vh)] rounded-[1.6rem] bg-white/10 motion-reduce:animate-none"
                  />
                )}
                {item.type === "video" ? (
                  <video
                    key={cur}
                    ref={(v) => { if (v && v.readyState >= 3) markReady(); }}
                    src={item.src}
                    poster={item.poster}
                    aria-label={item.alt || "Walkthrough video"}
                    controls
                    autoPlay={!reduceMotion}
                    muted
                    playsInline
                    preload="metadata"
                    onCanPlay={markReady}
                    onError={markReady}
                    className={cn(MEDIA, "bg-black", ready ? "opacity-100" : "opacity-0")}
                  />
                ) : (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    key={`${cur}-${original}`}
                    ref={(img) => { if (img?.complete && img.naturalWidth > 0) markReady(); }}
                    src={(!original && screenThumb(item.src, 800)) || item.src}
                    alt={item.alt || "Screenshot"}
                    draggable={false}
                    onLoad={markReady}
                    onError={() => (original || !screenThumb(item.src, 800) ? markReady() : setOriginal(true))}
                    className={cn(MEDIA, "select-none", ready ? "opacity-100" : "opacity-0")}
                  />
                )}
              </div>

              <div className="flex items-center gap-3 text-white/90">
                {many && (
                  <span className="whitespace-nowrap text-xs font-medium tabular-nums text-white/60">
                    {cur + 1} / {items.length}
                  </span>
                )}
                {item.caption && <span className="text-sm font-medium">{item.caption}</span>}
                {item.href && (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    {item.hrefLabel || "View source"}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
                {item.href2 && (
                  <a
                    href={item.href2}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    {item.href2Label || "View source"}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>,
    document.body
  );
}

const MEDIA =
  "absolute inset-0 m-auto max-h-full max-w-full rounded-[1.6rem] shadow-2xl ring-1 ring-white/10 object-contain transition-opacity duration-300 motion-reduce:transition-none";

function ArrowBtn({ dir, onClick }: { dir: "left" | "right"; onClick: (e: React.MouseEvent) => void }) {
  const Icon = dir === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === "left" ? "Previous screen" : "Next screen"}
      className={cn(
        "absolute top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full",
        "bg-white/10 text-white hover:bg-white/20 backdrop-blur-md transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
        dir === "left" ? "left-3 sm:left-5" : "right-3 sm:right-5"
      )}
    >
      <Icon className="h-6 w-6" />
    </button>
  );
}

export default ScreenLightbox;
